import React, {forwardRef, useState} from "react";

import blob from "@images/home/blue-blob.png";
import check from "@images/home/features/check.svg";
import minus from "@images/home/features/minus.svg";
import plus from "@images/home/features/plus.svg";
import stars from "@images/home/features/stars.svg";
import {RootDesc} from "@ui/descriptions/RootDesc";
import {BorderArticle} from "@ui/general/BorderArticle";
import {motion} from "framer-motion";
import {useSelector} from "react-redux";

import styles from "./styles.module.scss";

const itemAnimation = {
  hidden: {y: 50, opacity: 0},
  visible: (custom) => ({
    y: 0,
    opacity: 1,
    transition: {
      delay: custom * 0.3,
    },
  }),
};

const AccordeonItem = forwardRef(({item, mode = "default"}, ref) => {
  const [open, setOpen] = useState(false);
  const {isMobile} = useSelector((state) => state.common.settings);

  const handleClickItem = () => {
    if (item.body) setOpen(!open);
  };

  return (
    <li
      key={item.id}
      ref={ref}
    >
      <div
        onClick={handleClickItem}
        className={`${styles.accordeon_item} ${
          mode === "savings" ? styles.accordeon_item_savings : ""
        }`}
      >
        <div className={styles.accordeon_item_desc}>
          <div className={styles.accordeon_item_head}>
            {mode === "savings" ? (
              <h3 dangerouslySetInnerHTML={{__html: item.head}} />
            ) : (
              <h3>{item.head}</h3>
            )}

            {item.body && (
              <img
                src={open ? minus : plus}
                alt={"icon"}
              />
            )}
          </div>

          {mode === "savings" && isMobile && (
            <div className={styles.accordeon_item_value}>
              <span dangerouslySetInnerHTML={{__html: item.valueLabel}} />
              <b dangerouslySetInnerHTML={{__html: item.value}} />
            </div>
          )}

          {open && (
            <div className={styles.accordeon_item_body}>
              <RootDesc>
                {item.bodyIsHtml ? (
                  <span dangerouslySetInnerHTML={{__html: item.body}} />
                ) : (
                  <>{item.body}</>
                )}
              </RootDesc>
            </div>
          )}
        </div>

        {mode === "savings" && !isMobile && (
          <div className={styles.accordeon_item_value}>
            <span dangerouslySetInnerHTML={{__html: item.valueLabel}} />
            <b dangerouslySetInnerHTML={{__html: item.value}} />
          </div>
        )}

        {mode !== "savings" && !isMobile && (
          <>
            <div className={styles.accordeon_item_check}>
              {item.check_1 && (
                <img
                  src={check}
                  alt={"check"}
                />
              )}
            </div>

            <div className={styles.accordeon_item_check}>
              {item.check_2 && (
                <img
                  src={check}
                  alt={"check"}
                />
              )}
            </div>
          </>
        )}
      </div>
    </li>
  );
});

export const MAccordeonItem = motion(AccordeonItem);

export const Accordeon = (props) => {
  const {title, tableHead, items, subtitle, mode = "default"} = props;
  const {isMobile} = useSelector((state) => state.common.settings);
  const [showAdvanced, setShowAdvanced] = useState(false);
  const isSavingsMode = mode === "savings";

  const standardItems = items.filter(
    (item) => item.check_1 === true && item.check_2 === true,
  );
  const advancedItems = items.filter(
    (item) => !(item.check_1 === true && item.check_2 === true),
  );

  const handleCheckboxChange = () => {
    setShowAdvanced(!showAdvanced);
  };

  return (
    <div className={styles.accordeon}>
      {mode !== "savings" && (
        <img
          className={styles.blob}
          src={blob}
          alt={"blob"}
        />
      )}

      {title && (
        <div className={styles.accordeon_title}>
          <h2 dangerouslySetInnerHTML={{__html: title}} />

          {!isSavingsMode && (
            <img
              src={stars}
              alt={"icon"}
            />
          )}
        </div>
      )}

      {!isMobile ? (
        <div
          className={styles.accordeon_table}
          style={mode === "savings" ? {width: "100%", gap: "0"} : {}}
        >
          <BorderArticle>
            <motion.ul
              initial={"hidden"}
              whileInView={"visible"}
              viewport={{amount: 0, once: true}}
            >
              {!isSavingsMode && (
                <li>
                  <div
                    className={`${styles.accordeon_item} ${styles.accordeon_item_th}`}
                  >
                    {tableHead &&
                      tableHead.length > 0 &&
                      tableHead.map((head, index) => (
                        <React.Fragment key={index}>
                          <RootDesc>
                            <b>{head}</b>
                          </RootDesc>
                        </React.Fragment>
                      ))}
                  </div>
                </li>
              )}

              {items &&
                items.length > 0 &&
                items.map((item) => (
                  <React.Fragment key={item.id}>
                    <MAccordeonItem
                      custom={item.id + 1}
                      variants={itemAnimation}
                      item={item}
                      mode={mode}
                    />
                  </React.Fragment>
                ))}
            </motion.ul>
          </BorderArticle>

          <RootDesc>
            <span>{subtitle}</span>
          </RootDesc>
        </div>
      ) : (
        <div className={styles.accordeon_table_mobile}>
          <BorderArticle>
            {!isSavingsMode && (
              <div className={styles.accordeon_table_head}>
                <span>{tableHead[1]}</span>

                <BorderArticle>
                  <label htmlFor={"check-features"}>
                    <input
                      id={"check-features"}
                      type={"checkbox"}
                      onChange={handleCheckboxChange}
                    />

                    <i />
                  </label>
                </BorderArticle>

                <span>{tableHead[2]}</span>
              </div>
            )}

            <motion.ul
              initial={"hidden"}
              whileInView={"visible"}
              viewport={{amount: 0, once: true}}
              className={styles.accordeon_table_body}
            >
              {!isSavingsMode && (
                <li>
                  <div
                    className={`${styles.accordeon_item} ${styles.accordeon_item_th}`}
                  >
                    <RootDesc>
                      <b>{tableHead[0]}</b>
                    </RootDesc>
                  </div>
                </li>
              )}

              {(isSavingsMode
                ? items
                : showAdvanced
                  ? [...standardItems, ...advancedItems]
                  : standardItems
              ).map((item) => (
                <React.Fragment key={item.id}>
                  <AccordeonItem
                    item={item}
                    mode={mode}
                  />
                </React.Fragment>
              ))}
            </motion.ul>
          </BorderArticle>

          <RootDesc>
            <span>{subtitle}</span>
          </RootDesc>
        </div>
      )}
    </div>
  );
};
